import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getCourier } from '../state/courierSlice';
import { WarehousePricingPlan } from '@shared/sdk-v1';

interface CalculatorState {
  packageType: string;
  shippingMethod: string;
  weight: string;
  length: string;
  width: string;
  height: string;
  itemValue: string;
  isExpress: boolean;
  isHalfPound: boolean;
  insuranceOptIn: boolean;
  processingFee: string;
  freightCharge: string;
  customsEstimate: string;
  totalCharges: string;
  calculated: boolean;
  warehousingFee: string;
  inspectionFee: string;
  shippingBagFee: string;
}

interface CalculatorResults {
  processingFee: string;
  freightCharge: string;
  customsEstimate: string;
  totalCharges: string;
  warehousingFee: string;
  inspectionFee: string;
  shippingBagFee: string;
}

export const useCalculator = () => {
  const courier = useSelector(getCourier);
  const settings = courier?.courierSetting;

  const [warehousePlan] = useState<WarehousePricingPlan | null>(null);
  const [warehousePlanLoading] = useState<boolean>(false);
  const [isWarehouseMode] = useState<boolean>(false);
  const warehouseMaxWeight = 100;

  // Form state
  const [state, setState] = useState<CalculatorState>({
    packageType: 'Regular Package',
    shippingMethod: 'Air Shipping',
    weight: '',
    length: '',
    width: '',
    height: '',
    itemValue: '',
    isExpress: false,
    isHalfPound: false,
    insuranceOptIn: false,
    processingFee: '0',
    freightCharge: '0',
    customsEstimate: '0',
    totalCharges: '0',
    calculated: false,
    warehousingFee: '0',
    inspectionFee: '0',
    shippingBagFee: '0',
  });

  // Computed properties
  const isBarrelType = state.packageType === 'Barrels';
  const isWeightRequired = state.shippingMethod === 'Air Shipping';
  const areDimensionsRequired = state.shippingMethod === 'Sea Shipping';

  // Helper function to round weight based on courier setting
  const roundWeight = (weight: number): number => {
    if (!settings) return Math.ceil(weight);
    return settings.weightRoundingMode === 'down'
      ? Math.floor(weight)
      : Math.ceil(weight);
  };

  // Calculate package price based on weight and settings
  const calculatePackagePrice = (): number => {
    if (!settings) return 0;

    if (isBarrelType) {
      return settings.barrelPrice || 0;
    }

    if (state.shippingMethod === 'Sea Shipping') {
      const lengthNum = parseFloat(state.length) || 0;
      const widthNum = parseFloat(state.width) || 0;
      const heightNum = parseFloat(state.height) || 0;
      const cubicFoot = Math.ceil((lengthNum * widthNum * heightNum) / 1728);
      return cubicFoot * (settings.pricePerCubicFoot || 0);
    }

    const weightNum = parseFloat(state.weight) || 0;
    if (settings.useHalfPound && state.isHalfPound) {
      return settings.halfPoundPrice || 450;
    }

    const weightPrice = state.isExpress ? settings.expressWeightPrice : settings.weightPrice;
    // Round weight based on courier setting for price lookup
    const roundedWeight = roundWeight(weightNum);
    if (!Array.isArray(weightPrice) || roundedWeight <= 0 || roundedWeight > weightPrice.length) {
      return 0;
    }

    const basePrice = weightPrice[roundedWeight - 1] || 0;
    // exchangeRate is a JMD<->USD rate, so it only applies to USD couriers
    // (prices are stored in JMD). Other currencies (GBP, TTD, ...) store prices
    // in their own currency and get no JMD/USD conversion.
    const applyUsdFx =
      (settings.currency ?? (settings.useJmd ? 'JMD' : 'USD')) === 'USD';
    return basePrice * (applyUsdFx ? settings.exchangeRate || 1 : 1);
  };

  // Calculate processing fee
  const calculateProcessingFee = (): number => {
    if (!settings) return 0;

    // For barrels, processing fee is 0
    if (isBarrelType) {
      return 0;
    }

    let processingFee = 0;
    const weightNum = parseFloat(state.weight) || 0;
    // Round weight based on courier setting for fee calculations
    const roundedWeight = roundWeight(weightNum);
    const valueNum = parseFloat(state.itemValue) || 0;
    const customsLimit = 100;

    // Weight-based processing fee
    if (roundedWeight <= (settings.processingFeeWeightLimit || 0)) {
      processingFee = settings.processingFee3 || 0;
    } else {
      processingFee = settings.processingFee4 || 0;
    }

    // Value-based processing fee
    if (valueNum < customsLimit) {
      processingFee += settings.processingFee1 || 0;
    } else {
      processingFee += settings.processingFee2 || 0;
    }

    // Apply the JMD<->USD exchange rate only for USD couriers (see above); other
    // currencies store fees in their own currency and get no conversion.
    const applyUsdFx =
      (settings.currency ?? (settings.useJmd ? 'JMD' : 'USD')) === 'USD';
    if (applyUsdFx) {
      processingFee = processingFee * (settings.exchangeRate || 1);
    }

    return processingFee;
  };

  // Calculate customs fee. `tariffCustomsUsd` is the tariff-code estimate
  // (isCustomsCalculatorEnabled) and replaces the flat fixedCustomRate guess.
  const calculateCustomsFee = (tariffCustomsUsd?: number): number => {
    // For barrels, customs fee is 0
    if (isBarrelType) {
      return 0;
    }
    const valueNum = parseFloat(state.itemValue) || 0;
    // Base customs in USD terms, using fixedCustomRate if provided
    const customsRate = settings?.fixedCustomRate ?? 0.15;
    const baseCustomsUsd =
      tariffCustomsUsd !== undefined
        ? tariffCustomsUsd
        : valueNum >= 100 ? valueNum * customsRate : 0;
    // Always convert from USD to JMD using exchange rate
    return baseCustomsUsd * (settings?.exchangeRate || 1);
  };

  // Calculate total charges
  const calculateCosts = (tariffCustomsUsd?: number): CalculatorResults => {
    if (!settings) {
      return {
        processingFee: '0',
        freightCharge: '0',
        customsEstimate: '0',
        totalCharges: '0',
        warehousingFee: '0',
        inspectionFee: '0',
        shippingBagFee: '0',
      };
    }

    const packagePrice = calculatePackagePrice();
    const processingFeeValue = calculateProcessingFee();
    const customsValue = calculateCustomsFee(tariffCustomsUsd);
    let totalValue = packagePrice + processingFeeValue + customsValue;

    // Add insurance fee if opted in
    if (state.insuranceOptIn && settings.insuranceFee) {
      totalValue += settings.insuranceFee;
    }

    return {
      processingFee: processingFeeValue.toFixed(2),
      freightCharge: packagePrice.toFixed(2),
      customsEstimate: customsValue.toFixed(2),
      totalCharges: totalValue.toFixed(2),
      warehousingFee: '0',
      inspectionFee: '0',
      shippingBagFee: '0',
    };
  };

  // Form validation
  const isFormValid = (): boolean => {
    if (isBarrelType) return true;
    if (state.shippingMethod === 'Air Shipping') {
      return state.weight.trim() !== '' && state.itemValue.trim() !== '';
    } else {
      return state.length.trim() !== '' && state.width.trim() !== '' &&
             state.height.trim() !== '' && state.itemValue.trim() !== '';
    }
  };

  // Handle package type change
  useEffect(() => {
    if (isBarrelType) {
      setState(prev => ({
        ...prev,
        processingFee: '0',
        freightCharge: settings?.barrelPrice?.toString() || '0',
        customsEstimate: '0',
        totalCharges: settings?.barrelPrice?.toString() || '0',
        calculated: true,
        warehousingFee: '0',
        inspectionFee: '0',
        shippingBagFee: '0',
      }));
    } else {
      setState(prev => ({
        ...prev,
        calculated: false,
      }));
    }
  }, [state.packageType, settings?.barrelPrice]);

  // Update form state
  const updateField = (field: keyof CalculatorState, value: string | boolean) => {
    setState(prev => ({ ...prev, [field]: value }));
  };

  // Handle form submission
  const handleCalculate = (tariffCustomsUsd?: number) => {
    if (!isFormValid()) return;

    const results = calculateCosts(tariffCustomsUsd);
    setState(prev => ({
      ...prev,
      ...results,
      calculated: true,
    }));
  };

  return {
    // State
    state,
    settings,
    courier,
    isBarrelType,
    isWeightRequired,
    areDimensionsRequired,
    // Warehouse (disabled — always off, kept for API compatibility with Calculator page)
    isWarehouseMode,
    warehousePlan,
    warehousePlanLoading,
    warehouseMaxWeight,

    // Actions
    updateField,
    handleCalculate,
    isFormValid,
  };
};
